import {
  IAssistantConversation,
  IAssistantConversationSummary,
  IAssistantMessageRequest,
  IAssistantMessageResponse,
  IAssistantNewConversationRequest,
  IAssistantProposalStatusResponse,
  IAssistantReportRequest,
  IAssistantSendEmailRequest,
  IAssistantSuggestionRunResponse,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

type AssistantQueryArgs = {
  organisationId: string;
};

const conversationTag = { type: CacheTags.Assistant, id: `CONVERSATION` } as const;
const historyTag = { type: CacheTags.Assistant, id: `LIST` } as const;
const suggestionsTag = { type: CacheTags.Assistant, id: `SUGGESTIONS` } as const;

/** Admin override sessions pick read-only test runs (hidden from the org's users); users always get their own */
type SuggestionsQueryArgs = AssistantQueryArgs & { readOnly: boolean };

const suggestionsQuery = (url: string, method: `GET` | `POST`) => ({ organisationId, readOnly }: SuggestionsQueryArgs) => ({
  url,
  method,
  params: { readOnly },
  headers: {
    [orgHeader]: organisationId,
  },
});

export const assistantApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getAssistantConversation: builder.query<IAssistantConversation, AssistantQueryArgs>({
      query: ({ organisationId }) => ({
        url: `assistant/conversation`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [conversationTag],
    }),
    getAssistantConversations: builder.query<IAssistantConversationSummary[], AssistantQueryArgs>({
      query: ({ organisationId }) => ({
        url: `assistant/conversations`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [historyTag],
    }),
    getAssistantConversationById: builder.query<IAssistantConversation, AssistantQueryArgs & { conversationId: number }>({
      query: ({ organisationId, conversationId }) => ({
        url: `assistant/conversations/${conversationId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [historyTag],
    }),
    sendAssistantMessage: builder.mutation<IAssistantMessageResponse, AssistantQueryArgs & IAssistantMessageRequest>({
      query: ({ organisationId, ...data }) => ({
        url: `assistant/conversation/message`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      // The server answers at once with the user message and a `running` assistant
      // placeholder; the drawer polls the conversation until it is filled in
      async onQueryStarted({ organisationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(assistantApi.util.updateQueryData(`getAssistantConversation`, { organisationId }, draft => {
            draft.messages.push(...data.messages);
          }));
        }
        catch {
          // errors are handled by mutation callers
        }
      },
    }),
    newAssistantConversation: builder.mutation<IAssistantConversation, AssistantQueryArgs & IAssistantNewConversationRequest>({
      query: ({ organisationId, ...data }) => ({
        url: `assistant/conversation/new`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: [conversationTag, historyTag],
    }),
    applyAssistantProposal: builder.mutation<IAssistantProposalStatusResponse, AssistantQueryArgs & { messageId: number }>({
      query: ({ organisationId, messageId }) => ({
        url: `assistant/conversation/messages/${messageId}/apply`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        timeout: 120000,
      }),
      // Refresh the conversation (apply may have marked the proposal failed), everything policies touch, and settings (safe mode)
      invalidatesTags: [
        conversationTag,
        historyTag,
        { type: CacheTags.Policies, id: `LIST` },
        { type: CacheTags.Groups, id: `LIST` },
        { type: CacheTags.SystemPolicies, id: `LIST` },
        { type: CacheTags.Settings, id: `LIST` },
      ],
    }),
    giveAssistantConsent: builder.mutation<{ aiConsentGiven: true }, AssistantQueryArgs>({
      query: ({ organisationId }) => ({
        url: `assistant/consent`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // The org list carries `aiConsentGiven`, so refetch it to lift the gate
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    reportAssistantMessage: builder.mutation<{ reported: true }, AssistantQueryArgs & IAssistantReportRequest & { messageId: number }>({
      query: ({ organisationId, messageId, ...data }) => ({
        url: `assistant/conversation/messages/${messageId}/report`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
    }),
    getAssistantSuggestions: builder.query<IAssistantSuggestionRunResponse, SuggestionsQueryArgs>({
      query: suggestionsQuery(`assistant/suggestions/latest`, `GET`),
      providesTags: [suggestionsTag],
    }),
    generateAssistantSuggestions: builder.mutation<IAssistantSuggestionRunResponse, SuggestionsQueryArgs>({
      query: suggestionsQuery(`assistant/suggestions/generate`, `POST`),
      invalidatesTags: [suggestionsTag],
    }),
    applyAssistantSuggestion: builder.mutation<IAssistantSuggestionRunResponse, SuggestionsQueryArgs & { runId: number; index: number }>({
      query: ({ runId, index, ...args }) => ({ ...suggestionsQuery(`assistant/suggestions/${runId}/${index}/apply`, `POST`)(args), timeout: 180000 }),
      // Actions and policy changes touch most of the dashboard, so refetch what they can change
      invalidatesTags: [
        suggestionsTag,
        { type: CacheTags.Policies, id: `LIST` },
        { type: CacheTags.Groups, id: `LIST` },
        { type: CacheTags.SystemPolicies, id: `LIST` },
        { type: CacheTags.Settings, id: `LIST` },
        { type: CacheTags.Actions },
      ],
    }),
    /** Marks a suggestion done after it ran through the app's own send dialog */
    completeAssistantSuggestion: builder.mutation<IAssistantSuggestionRunResponse, SuggestionsQueryArgs & { runId: number; index: number; results: string[] }>({
      query: ({ runId, index, results, ...args }) => ({
        ...suggestionsQuery(`assistant/suggestions/${runId}/${index}/complete`, `POST`)(args),
        data: { results },
      }),
      invalidatesTags: [suggestionsTag],
    }),
    dismissAssistantSuggestion: builder.mutation<IAssistantSuggestionRunResponse, SuggestionsQueryArgs & { runId: number; index: number }>({
      query: ({ runId, index, ...args }) => suggestionsQuery(`assistant/suggestions/${runId}/${index}/dismiss`, `POST`)(args),
      invalidatesTags: [suggestionsTag],
    }),
    sendAssistantEmailDraft: builder.mutation<IAssistantProposalStatusResponse, AssistantQueryArgs & IAssistantSendEmailRequest & { messageId: number }>({
      query: ({ organisationId, messageId, ...data }) => ({
        url: `assistant/conversation/messages/${messageId}/send-email`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
        // Sending runs a spam check and fetches the invoice PDF inline
        timeout: 120000,
      }),
      invalidatesTags: [conversationTag, historyTag, { type: CacheTags.Communications, id: `LIST` }, { type: CacheTags.Actions }],
    }),
    dismissAssistantProposal: builder.mutation<IAssistantProposalStatusResponse, AssistantQueryArgs & { messageId: number }>({
      query: ({ organisationId, messageId }) => ({
        url: `assistant/conversation/messages/${messageId}/dismiss`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [conversationTag, historyTag],
    }),
  }),
});

export const {
  useGetAssistantConversationQuery,
  useGetAssistantConversationsQuery,
  useGetAssistantConversationByIdQuery,
  useSendAssistantMessageMutation,
  useNewAssistantConversationMutation,
  useApplyAssistantProposalMutation,
  useDismissAssistantProposalMutation,
  useSendAssistantEmailDraftMutation,
  useGiveAssistantConsentMutation,
  useReportAssistantMessageMutation,
  useGetAssistantSuggestionsQuery,
  useGenerateAssistantSuggestionsMutation,
  useApplyAssistantSuggestionMutation,
  useDismissAssistantSuggestionMutation,
  useCompleteAssistantSuggestionMutation,
} = assistantApi;
